import cameraThumbnail from "../../../../images/svg/recording-tool-thumbnail-camera.svg";
import screenThumbnail from "../../../../images/svg/recording-tool-thumbnail-screen.svg";
import cameraScreenThumbnail from "../../../../images/svg/recording-tool-thumbnail-camera-screen.svg";
import micScreenThumbnail from "../../../../images/svg/recording-tool-thumbnail-mic-screen.svg";
import type { MediaStreamType } from "../../../editor/Hooks";
import { Box, ButtonBase, Popover, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import React from "react";
import { ThumbnailActions } from "@sundaysky/smartvideo-hub-truffle-component-library";
import { useRecordingToolMenuAnchor } from "../../hooks/RecordingTool/UseRecordingToolMenuAnchor";
import { localEditorSelectedLibraryVar } from "../../../editor/State";

export enum RecordingType {
    CAMERA = "Camera",
    SCREEN = "Screen",
    CAMERA_SCREEN = "Camera & screen",
    MIC_SCREEN = "Mic & screen"
}

type RecordingMenuThumbnail = {
    src: string;
    type: RecordingType;
    trackingName: string;
};

const gridThumbnails: RecordingMenuThumbnail[] = [
    { src: cameraThumbnail, type: RecordingType.CAMERA, trackingName: "webcam" },
    { src: screenThumbnail, type: RecordingType.SCREEN, trackingName: "screen" },
    { src: cameraScreenThumbnail, type: RecordingType.CAMERA_SCREEN, trackingName: "webcam-and-screen" },
    { src: micScreenThumbnail, type: RecordingType.MIC_SCREEN, trackingName: "mic-and-screen" }
];

const isFeatureAvailable = (type: RecordingType) => {
    return type === RecordingType.CAMERA || type === RecordingType.SCREEN;
};

type RecordingMenuPopoverProps = {
    startRecordingToolSession: (type: MediaStreamType) => Promise<void>;
}

export const RecordingMenuPopover = (props: RecordingMenuPopoverProps) => {
    const { setRecordingToolMenuAnchor, recordingToolMenuAnchor } = useRecordingToolMenuAnchor();

    const recordingActions: Record<RecordingType, () => Promise<void>> = {
        [RecordingType.SCREEN]: async () => await props.startRecordingToolSession("screen"),
        [RecordingType.CAMERA]: async () => await props.startRecordingToolSession("camera"),
        [RecordingType.CAMERA_SCREEN]: async () => { /* TODO: Implement camera & screen recording */ },
        [RecordingType.MIC_SCREEN]: async () => { /* TODO: Implement mic & screen recording */ }
    };

    const handleRecordingTypeSelect = async (type: RecordingType) => {
        if (!isFeatureAvailable(type)) {
            return;
        }

        localEditorSelectedLibraryVar({ library: null, opener: null });
        setRecordingToolMenuAnchor(null);
        await recordingActions[type]();
    };

    return <Popover
        open={Boolean(recordingToolMenuAnchor)}
        onClose={() => setRecordingToolMenuAnchor(null)}
        anchorEl={recordingToolMenuAnchor}
        anchorOrigin={{
            vertical: "top",
            horizontal: "right"
        }}
        transformOrigin={{
            vertical: "top",
            horizontal: "left"
        }}
        slotProps={{ paper: { sx: { width: "292px" } } }}
    >
        <Box sx={{ padding: "8px" }}>
            <Grid container spacing={2} columns={2}>
                {gridThumbnails.map((thumbnail, index) => (
                    <Grid xs={1} key={index}>
                        <ButtonBase
                            onClick={() => handleRecordingTypeSelect(thumbnail.type)}
                            data-tracking-id={`tracking-id-recorder-open-${thumbnail.trackingName}-rec`}
                            disabled={!isFeatureAvailable(thumbnail.type)}
                        >
                            <ThumbnailActions
                                ContentProps={{ sx: { width: "130px", height: "73px" } }}
                                showActions={"onHover"}
                                label={<Typography sx={{ flexGrow: 1 }} variant={"body1"}>{thumbnail.type}</Typography>}
                            >
                                <img
                                    src={thumbnail.src}
                                    alt={thumbnail.type}
                                />
                            </ThumbnailActions>
                        </ButtonBase>
                    </Grid>
                ))}
            </Grid>
        </Box>
    </Popover>;
};
